import type { Capabilities, PlatformCapabilities } from "@eduleo/shared";
import {
  House,
  LayoutDashboard,
  type LucideIcon,
  MapPinned,
  ReceiptIndianRupee,
  School,
  Settings,
  Shapes,
  ShieldCheck,
  Users,
  UsersRound,
} from "lucide-react";

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
  /** Rarely used: in the menu (on phones, behind "More") but not a bottom tab. */
  menuOnly?: boolean;
}

/** Eduleo's own team gets the platform console instead of a school's modules. */
function platformNavItems(platform: PlatformCapabilities): NavItem[] {
  const items: NavItem[] = [
    { to: "/platform", label: "Overview", icon: LayoutDashboard, end: true },
  ];
  if (platform.schools.view)
    items.push({ to: "/platform/schools", label: "Schools", icon: School });
  if (platform.billing.view)
    items.push({ to: "/platform/invoices", label: "Invoices", icon: ReceiptIndianRupee });
  if (platform.team.view) items.push({ to: "/platform/team", label: "Team", icon: UsersRound });
  if (platform.regions.view)
    items.push({ to: "/platform/regions", label: "Regions", icon: MapPinned });
  // Everyone's own profile, notifications and sign-in; Eduleo's details for those who may.
  items.push({ to: "/platform/settings", label: "Settings", icon: Settings, menuOnly: true });
  return items;
}

/**
 * The menu shows only what the signed-in role can open (design system rule).
 * Add a module here together with its capability flag from GET /auth/me.
 */
export function navItemsFor(capabilities: Capabilities | undefined): NavItem[] {
  if (capabilities?.platform) return platformNavItems(capabilities.platform);
  const items: NavItem[] = [{ to: "/", label: "Home", icon: House, end: true }];
  if (capabilities?.students.list) items.push({ to: "/students", label: "Students", icon: Users });
  if (capabilities?.academics?.view) {
    items.push({ to: "/settings/classes", label: "Classes", icon: Shapes, menuOnly: true });
  }
  if (capabilities?.people?.view || capabilities?.roles?.view) {
    items.push({
      to: capabilities.people?.view ? "/settings/people" : "/settings/people/roles",
      label: "Staff and roles",
      icon: ShieldCheck,
      menuOnly: true,
    });
  }
  return items;
}
